import { useMemo, useState } from 'react';

import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { cn } from '@/lib/utils';
import { getGroupByLabel } from '@/modules/analytics/constants/metricsCatalog';
import type { ValueFormatter } from '@/modules/analytics/hooks/useReportMetric';
import type { ReportMetricTableRow } from '@/modules/analytics/hooks/useReportMetricTable';
import { groupDisplayLabel, groupValueLink } from '@/modules/analytics/utils/analyticsLinks';
import { selectAccountSlug } from '@/redux/session/selectors';
import { ROW_HIGHLIGHT_BASE } from '@/util/constants';

const defaultValueFormatter: ValueFormatter = (value) => value.toLocaleString();

type ReportMetricTableProps = {
  rows: ReportMetricTableRow[];
  groupColumns: string[];
  valueLabel: string;
  isLoading: boolean;
  isError: boolean;
  /** rowKey → color, shared with the chart so the two views stay in sync. */
  colorByKey: Map<string, string>;
  /** Formats the value cell. Defaults to locale-string. */
  valueFormatter?: ValueFormatter;
  valueLink?: (row: ReportMetricTableRow) => string | null;
};

const SKELETON_ROWS = 3;

const VALUE_COLUMN_ID = '__value__';

const HeaderLabel = ({ children }: { children: string }) => (
  <span className="min-w-0 truncate">{children}</span>
);

const GroupLabel = ({ link, label }: { link: string | null; label: string }) => {
  const text = <TruncatedText text={label} className="block text-sm leading-5 text-foreground" />;

  if (!link) {
    return text;
  }
  return (
    <Link to={link} className="block min-w-0 hover:underline">
      {text}
    </Link>
  );
};

const ValueCell = ({
  link,
  value,
  label,
}: {
  link: string | null;
  value: string;
  label: string;
}) => {
  if (!link) {
    return <span className="text-sm leading-5 text-foreground tabular-nums">{value}</span>;
  }
  return (
    <Link
      to={link}
      aria-label={label}
      className="text-sm leading-5 text-foreground tabular-nums hover:underline"
    >
      {value}
    </Link>
  );
};

export const ReportMetricTable = ({
  rows,
  groupColumns,
  valueLabel,
  isLoading,
  isError,
  colorByKey,
  valueFormatter = defaultValueFormatter,
  valueLink,
}: ReportMetricTableProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const [sorting, setSorting] = useState<SortingState>([{ id: VALUE_COLUMN_ID, desc: true }]);

  const columns = useMemo<ColumnDef<ReportMetricTableRow>[]>(
    () => [
      ...groupColumns.map<ColumnDef<ReportMetricTableRow>>((column, idx) => ({
        id: column,
        accessorFn: (row) => groupDisplayLabel(column, row.groupValues[column], t),
        header: ({ column: col }) => (
          <SortableHeader
            column={col}
            className="max-w-full"
            ariaLabel={getGroupByLabel(column) ?? column}
          >
            <HeaderLabel>{getGroupByLabel(column) ?? column}</HeaderLabel>
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const color = idx === 0 ? colorByKey.get(row.original.rowKey) : undefined;
          const groupValue = row.original.groupValues[column];
          const label = groupDisplayLabel(column, groupValue, t);
          const link = groupValueLink(slug, column, groupValue?.key ?? '', !!groupValue?.isMissing);
          return (
            <span className="flex min-w-0 items-center gap-2">
              {idx === 0 && (
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: color ?? 'transparent' }}
                />
              )}
              <GroupLabel link={link} label={label} />
            </span>
          );
        },
        meta: idx === 0 ? { className: 'pl-6' } : undefined,
      })),
      {
        id: VALUE_COLUMN_ID,
        accessorKey: 'value',
        header: ({ column }) => (
          <SortableHeader column={column} className="ml-auto max-w-full" ariaLabel={valueLabel}>
            <HeaderLabel>{valueLabel}</HeaderLabel>
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <ValueCell
            link={valueLink?.(row.original) ?? null}
            value={valueFormatter(row.original.value)}
            label={t('analytics.reports_detail_open_conversations')}
          />
        ),
        meta: { isNumeric: true, className: 'pr-6' },
      },
    ],
    [groupColumns, valueLabel, colorByKey, valueFormatter, valueLink, slug, t]
  );

  // The compiler skips this component; only plain values reach its children.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getRowId: (row) => row.rowKey,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isError) {
    return (
      <p className="py-4 text-center text-sm leading-5 text-muted-foreground">
        {t('analytics.reports_detail_metric_query_error')}
      </p>
    );
  }

  if (isLoading) {
    return (
      <Table fixedLayout>
        <TableHeader>
          <TableRow className="h-12">
            {groupColumns.map((column, idx) => (
              <TableHead key={column} className={idx === 0 ? 'pl-6' : ''}>
                <HeaderLabel>{getGroupByLabel(column) ?? column}</HeaderLabel>
              </TableHead>
            ))}
            <TableHead className="pr-6 text-right">
              <HeaderLabel>{valueLabel}</HeaderLabel>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={i}>
              {groupColumns.map((column, idx) => (
                <TableCell key={column} className={idx === 0 ? 'pl-6' : ''}>
                  <Skeleton className="h-4 w-full max-w-40" />
                </TableCell>
              ))}
              <TableCell className="pr-6 text-right">
                <Skeleton className="ml-auto h-4 w-12" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Table fixedLayout className="max-h-152 overscroll-x-none overscroll-y-auto">
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id} className="h-12">
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                className={cn(
                  header.column.columnDef.meta?.isNumeric ? 'text-right' : 'text-left',
                  header.column.columnDef.meta?.className
                )}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id} className={ROW_HIGHLIGHT_BASE}>
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                className={cn(
                  cell.column.columnDef.meta?.isNumeric ? 'text-right' : 'text-left',
                  cell.column.columnDef.meta?.className
                )}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
